// src/components/ContactSection.js
import React, { useState } from 'react';
import NaverMap from './NaverMap';
import styled from 'styled-components';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { AnimatedSection } from './common/AnimatedSection';

const ContactSectionWrapper = styled.section`
  background: #ffffff;
  padding: 6rem 0;
`;

const Container = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 3rem 3rem; 

  @media (max-width: 768px) {
    padding-bottom: 3rem;
    padding: 0 1rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.3rem;
  font-weight: 700;
  color: rgba(17, 17, 17, 0.75);
  text-align: center;
  margin-bottom: 4rem;
  letter-spacing: -1px;

  /* ✅ 모바일에서만 좌측 정렬 & 살짝 축소 */
  @media (max-width: 768px) {
    text-align: left;
    font-size: 1.8rem;
    margin-bottom: 2rem;
    padding-left: 1rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.3rem;
  color: #4b5563;
  text-align: center;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    text-align: left;
    font-size: 1.05rem;
    margin-bottom: 2rem;
  }
`;

const ContactContainer = styled.div`
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(59, 131, 246, 0.1);

  /* 데스크탑 여백 유지, 모바일에만 내부 여백 추가 */
  @media (max-width: 900px) {
    padding: 1rem;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContactInfo = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 2.5rem;

  @media (max-width: 600px) {
    padding: 1rem;
  }
`;

const InfoTitle = styled.h3`
  font-size: 1.5rem;
  color: #3b82f6;
  margin-bottom: 2rem;
  font-weight: 700;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
  color: #222222;

  .icon {
    width: 24px;
    height: 24px;
    margin-right: 1rem;
    color: #3b82f6;
    font-size: 1.2rem;
    flex: 0 0 24px;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const MapArea = styled.div`
  background: #e5edfa;
  border-radius: 0;
  height: clamp(200px, 28vh, 320px);
  margin-top: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 1.1rem;
  border: 1px solid #e5e7eb;

  @media (max-width: 768px) {
    height: clamp(220px, 32vh, 340px); /* 모바일에서 살짝 더 여유 */
    font-size: 1rem;
  }
`;

const ContactForm = styled.form`
  background: #ffffff;
  border-radius: 20px;
  padding: 2.5rem;

  @media (max-width: 600px) {
    padding: 1rem;
  }
`;

const FormTitle = styled.h3`
  font-size: 1.5rem;
  color: #3b82f6;
  margin-bottom: 2rem;
  font-weight: 700;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr; /* 모바일 한 줄 스택 (원래 의도 유지) */
  }
`;

const InputField = styled.input`
  width: 100%;
  padding: 1rem 1.2rem;
  border: 1px solid #e5e7eb;
  border-radius: 0;
  font-size: 1rem;
  background: #f8fafc;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: #ffffff;
  }

  &::placeholder {
    color: #94a3b8;
  }

  @media (max-width: 768px) {
    padding: 0.9rem 1rem;
    font-size: 0.95rem;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem 1.2rem;
  border: 1px solid #e5e7eb;
  border-radius: 0;
  font-size: 1rem;
  background: #f8fafc;
  min-height: 180px;
  resize: vertical;
  margin-bottom: 2rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: #ffffff;
  }

  &::placeholder {
    color: #94a3b8;
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
    min-height: 160px;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: #60a5fa;
  color: #ffffff;
  padding: 1.2rem 2rem;
  border-radius: 0;
  font-size: 1.1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.2);

  &:hover {
    background: #3b82f6;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 768px) {
    padding: 1rem 1.4rem;
    font-size: 1rem;
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  color: #4b5563;

  input[type='checkbox'] {
    margin-right: 0.5rem;
  }
`;

const Msg = styled.p`
  margin-top: 12px;
  font-size: 0.95rem;
  color: ${({ error }) => (error ? '#b91c1c' : '#065f46')};

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    privacy: false,
  });

  const [sending, setSending] = useState(false);
  const [serverMsg, setServerMsg] = useState('');
  const [serverErr, setServerErr] = useState('');

  const [titleRef, titleVisible] = useScrollAnimation();
  const [containerRef, containerVisible] = useScrollAnimation();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const API_BASE = process.env.REACT_APP_API_BASE || ''; // CRA proxy 사용 시 빈 문자열

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerMsg('');
    setServerErr('');

    if (!formData.privacy) {
      setServerErr('개인정보처리방침에 동의해 주세요.');
      return;
    }
    if (!formData.name || !formData.email || !formData.message) {
      setServerErr('필수 항목(성함/이메일/문의내용)을 입력해 주세요.');
      return;
    }

    try {
      setSending(true);
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || '전송 실패');
      }
      setServerMsg('문의가 정상 접수되었습니다. 감사합니다!');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: '',
        privacy: false,
      });
    } catch (err) {
      console.error(err);
      setServerErr('문의 전송 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSending(false);
    }
  };

  return (
    <ContactSectionWrapper>
      <Container>
        <AnimatedSection ref={titleRef} isVisible={titleVisible} animation="fadeInUp">
          <SectionTitle>CONTACT</SectionTitle>
          {/* <SectionSubtitle>궁금한 사항이 있으시면 언제든지 문의해 주세요</SectionSubtitle> */}
        </AnimatedSection>

        <AnimatedSection
          ref={containerRef}
          isVisible={containerVisible}
          animation="scaleIn"
          delay="0.3s"
        >
          <ContactContainer>
            <ContactGrid>
              <ContactInfo>
                <InfoTitle>연락처 정보</InfoTitle>

                <InfoItem>
                  <span className="icon">📍</span>
                  <div>
                    <strong>주소</strong>
                    <br />
                    광주광역시 북구 하서로 672번길 18-17
                    <br />
                    (지동 806-9)
                  </div>
                </InfoItem>

                <InfoItem>
                  <span className="icon">📞</span>
                  <div>
                    <strong>전화번호</strong>
                    <br />
                    062-511-7741
                  </div>
                </InfoItem>

                <InfoItem>
                  <span className="icon">📠</span>
                  <div>
                    <strong>팩스</strong>
                    <br />
                    062-576-7741
                  </div>
                </InfoItem>

                <InfoItem>
                  <span className="icon">📧</span>
                  <div>
                    <strong>이메일</strong>
                    <br />
                    seyeon7741@daum.net
                  </div>
                </InfoItem>

                <MapArea>
                  <NaverMap />
                </MapArea>
              </ContactInfo>

              <ContactForm onSubmit={handleSubmit}>
                <FormTitle>문의하기</FormTitle>

                <FormGrid>
                  <InputField
                    type="text"
                    name="name"
                    placeholder="성함 *"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                  <InputField
                    type="email"
                    name="email"
                    placeholder="이메일 *"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </FormGrid>

                <FormGrid>
                  <InputField
                    type="tel"
                    name="phone"
                    placeholder="연락처"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                  <InputField
                    type="text"
                    name="company"
                    placeholder="회사명"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </FormGrid>

                <TextArea
                  name="message"
                  placeholder="문의 내용을 입력해 주세요 *"
                  value={formData.message}
                  onChange={handleChange}
                  required
                />

                <CheckboxWrapper>
                  <input
                    type="checkbox"
                    name="privacy"
                    checked={formData.privacy}
                    onChange={handleChange}
                    required
                  />
                  <label>개인정보처리방침에 동의합니다 *</label>
                </CheckboxWrapper>

                <SubmitButton type="submit" disabled={sending}>
                  {sending ? '전송 중...' : '문의 보내기'}
                </SubmitButton>

                {serverMsg && <Msg>{serverMsg}</Msg>}
                {serverErr && <Msg error>{serverErr}</Msg>}
              </ContactForm>
            </ContactGrid>
          </ContactContainer>
        </AnimatedSection>
      </Container>
    </ContactSectionWrapper>
  );
}

export default ContactSection;
